import FitAssistantStatus from '../Enums/FitAssistantStatus.ts';
import FitAssistantVariation from '../Enums/FitAssistantVariation.ts';
import FitAssistantStatusIcon from '../Enums/FitAssistantStatusIcon.ts';
import { CarInformation, Translations } from '../Types/FitAssistantData.ts';

export class ButtonHelper {
  public static GetSelectionText(
    fitStatus: FitAssistantStatus,
    carInformation: CarInformation[],
    translations: Translations,
    isProductPage: boolean
  ): string {
    if (!translations) return '';

    switch (fitStatus) {
      case FitAssistantStatus.NoSelection:
        return isProductPage ? translations.confirmVehicleFit : translations.bannerNoSelection;
      case FitAssistantStatus.Confirmed:
      case FitAssistantStatus.ConfirmedNoRacks:
      case FitAssistantStatus.Unconfirmed:
      case FitAssistantStatus.NoFit:
        return this.GetCarInformationBtnText(carInformation);
      case FitAssistantStatus.RackMounting:
        return translations.rackMounting;
      default:
        return '';
    }
  }

  public static GetStatusLabel(fitStatus: FitAssistantStatus, translations: Translations): string {
    let label = '';
    switch (fitStatus) {
      case FitAssistantStatus.NoSelection:
        label = translations.seeFits;
        break;
      case FitAssistantStatus.Confirmed:
      case FitAssistantStatus.RackMounting:
        label = translations.confirmed;
        break;
      case FitAssistantStatus.Unconfirmed:
        label = translations.unconfirmed;
        break;
      case FitAssistantStatus.NoFit:
        label = translations.noFit;
        break;
    }
    return label;
  }

  public static GetCarInformationBtnText(carInformation: CarInformation[]): string {
    if (carInformation && carInformation.length >= 3)
      return carInformation[0].text + ' ' + carInformation[1].text + ' ' + carInformation[2].text;
    return '';
  }

  public static HandleStickyButtonOnScroll(
    buttonRef: { readonly current: HTMLDivElement | null },
    setIsSticky: React.Dispatch<React.SetStateAction<boolean>>
  ): void {
    if (!buttonRef.current) return;

    const siteHeader = document.getElementById('REACT_siteHeader');
    if (!siteHeader) return;

    const headerHeight = siteHeader.getBoundingClientRect().height || 0;
    const headerIsSticky = siteHeader.querySelector('.siteHeader_wrapper.sticky');
    const rect = buttonRef.current.getBoundingClientRect();
    const shouldBeSticky = rect.top < (headerIsSticky ? headerHeight : 0);
    setIsSticky((prev) => {
      if (prev !== shouldBeSticky) {
        return shouldBeSticky;
      }
      return prev;
    });

    if (shouldBeSticky) siteHeader.querySelector('.siteHeader_wrapper')?.classList.add('collapsed');
  }

  public static HandleBannerVisibilityOnScroll(setIsPastLastItem: React.Dispatch<React.SetStateAction<boolean>>): void {
    const isotopeContainer = document.querySelector('.isotope-container');
    if (!isotopeContainer) return;

    const siteHeader = document.getElementById('REACT_siteHeader');
    const headerHeight = siteHeader?.getBoundingClientRect().height ?? 0;

    const listBottom = isotopeContainer.getBoundingClientRect().bottom;
    const isPastLastItem = listBottom < headerHeight;

    setIsPastLastItem((prev) => (prev !== isPastLastItem ? isPastLastItem : prev));
  }

  /**
   * Gets the icon class based on fit assistant status and variation type.
   * Handles three cases: rooftopTents, roofRack, and neither.
   * @param fitStatus - The current fit status
   * @param variation - The product variation type
   * @returns The icon class string
   */
  public static getIconClassByFitAssistantStatus(
    fitStatus: FitAssistantStatus,
    variation?: FitAssistantVariation
  ): string {
    // Determine the product type category
    const isRooftopTent = variation === FitAssistantVariation.RooftopTents;
    const isRoofRack =
      variation === FitAssistantVariation.CarRoofRacks ||
      variation === FitAssistantVariation.TruckRackSystem ||
      variation === FitAssistantVariation.VanRackSystems;

    // Get base icon based on status
    let iconClass = '';
    switch (fitStatus) {
      case FitAssistantStatus.NoSelection:
        iconClass = FitAssistantStatusIcon.NoSelection;
        break;
      case FitAssistantStatus.Confirmed:
      case FitAssistantStatus.RackMounting:
        iconClass = FitAssistantStatusIcon.Confirmed;
        break;
      case FitAssistantStatus.Unconfirmed:
        iconClass = FitAssistantStatusIcon.Unconfirmed;
        break;
      case FitAssistantStatus.NoFit:
        iconClass = FitAssistantStatusIcon.NoFit;
        break;
      default:
        iconClass = FitAssistantStatusIcon.NoSelection;
    }

    if (isRooftopTent) {
      iconClass = FitAssistantStatusIcon.Unconfirmed;
    } else if (isRoofRack) {
      iconClass = FitAssistantStatusIcon.NoSelectionRoofRack;
    }
    return iconClass;
  }
}
